import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, tap } from 'rxjs';
import { LabelModule } from '@progress/kendo-angular-label';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ToastService } from '../../services/toast/toast.service';
import { NotificationService } from '@progress/kendo-angular-notification';
import { FormErrorComponent } from '../../shared/components/form-error/form-error.component';
import { LocalStorageService } from '../../services/wrapper/local-storage.service';
import { signInWithRedirect } from '@aws-amplify/auth';
import { StorageKeys } from '../../services/wrapper/storage-keys';
import { TranslationService } from '../../services/translation/translation.service';
import { ReturnPathHelper } from '../../services/authentication/return-path-helper';
import {
  getOAuthParams,
  isOAuthParamsValid,
  OAuthParams,
  redirectToHostedUiAuthorize,
} from '../../services/authentication/oauth-redirect';
import { AfRoutes } from '../../routes';

@Component({
  selector: 'af-login',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputsModule,
    LayoutModule,
    LabelModule,
    ButtonsModule,
    FormErrorComponent,
    DatePipe,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  providers: [ToastService, NotificationService],
})
export class LoginComponent implements OnInit, OnDestroy {
 
  private readonly termsVersion : number = 1;
  private terms = false;

  loginForm!: FormGroup;

  routingSubscription = new Subscription();
  errorMessage?: string;
  oAuthParams?: OAuthParams;
  returnPath?: string;

  protected readonly date = Date.now();

  constructor(
    private localStorageService: LocalStorageService,
    private activatedRoute: ActivatedRoute,
    protected readonly translationService: TranslationService
  ) {}

  ngOnInit(): void {
    this.terms = !this.isTermsOutdated();

    const storedEmail = this.getEmailFromLocalStorage();
    this.loginForm = new FormGroup({
      username: new FormControl(storedEmail || '', [Validators.required, Validators.email]),
      remember: new FormControl(storedEmail !== null),
      terms: new FormControl(this.terms, [Validators.requiredTrue]),
    });

    // Check for ALB OAuth parameters in query string
    const oAuthParams = getOAuthParams(this.activatedRoute.snapshot.queryParams);
    if (oAuthParams) {
      if (this.isOAuthParamsValid(oAuthParams)) {
        this.oAuthParams = oAuthParams;
      } else {
        this.errorMessage = 'Invalid OAuth parameters.';
      }
    }

    const returnPath = this.activatedRoute.snapshot.queryParams['returnPath'];
    if (returnPath) {
      this.returnPath = returnPath;
    }

    // Rewrite url in browser
    history.replaceState(null, '', AfRoutes.login);

    this.loginForm.valueChanges
      .pipe(tap(() => this.clearErrorMessage()))
      .subscribe();
  }

  onSubmit() {
    this.loginForm.markAllAsTouched();
    
    if (!this.loginForm.valid) return;

    const username = this.loginForm.get('username')?.value;
    const terms = this.loginForm.get('terms')?.value;
    const remember = this.loginForm.get('remember')?.value;
    if (remember) {
      this.saveEmailToLocalStorage(username);
    } else {
      this.removeEmailFromLocalStorage();
    }
    if (terms === true) {
      this.saveAcceptedTermsVersion();
      if (this.oAuthParams) {
        this.redirectToOauthAuthorize(username);
      } else {
        // Default Amplify flow
        signInWithRedirect({
          options: {
            loginHint: username,
          },
          provider: {
            idpIdentifier: username.split('@')[1],
          },
          customState: this.returnPath
        });
      }
    }
  }

  private isTermsOutdated(): boolean {
    const acceptedTermsVersion = this.localStorageService.getItem(StorageKeys.termsVersion);
    return acceptedTermsVersion === null || parseInt(acceptedTermsVersion) < this.termsVersion;
  }

  private saveAcceptedTermsVersion(): void {
    this.localStorageService.setItem(StorageKeys.termsVersion, this.termsVersion.toString());
  }

  private saveEmailToLocalStorage(username: string): void {
    this.localStorageService.setItem(StorageKeys.rememberedEmail, username);
  }

  private getEmailFromLocalStorage(): string | null {
    return this.localStorageService.getItem(StorageKeys.rememberedEmail);
  }

  private removeEmailFromLocalStorage(): void {
    this.localStorageService.removeItem(StorageKeys.rememberedEmail);
  }

  private clearErrorMessage() {
    this.errorMessage = undefined;
  }

  private redirectToOauthAuthorize(username?: string): void {
    if (!this.oAuthParams) {
      return;
    }

    if (this.returnPath) {
      this.oAuthParams.state = ReturnPathHelper.appendReturnPathToState(this.oAuthParams.state, this.returnPath);
    }

    redirectToHostedUiAuthorize(this.oAuthParams, username);
  }

  private isOAuthParamsValid(params: OAuthParams): boolean {
    return isOAuthParamsValid(params);
  }

  ngOnDestroy(): void {
    this.routingSubscription.unsubscribe();
  }
}
